import moment from 'moment';
import {Types} from 'mongoose';
import {ToDoManagerApi} from '@/classes/api/to-do-manager.api.class';
import {showUndoToast, UndoOutcome} from '@/helpers/undoToast';
import {ToDoDataTypesEnum} from '@/enums/to-do-manager/ToDoDataTypes.enum';

/**
 * To-do actions with "Rückgängig" (Notify v2, A3/D1/D3).
 * - delete: soft delete + restore when the to-do-manager supports it, otherwise delayed delete (runs after the toast)
 * - complete / reopen: immediately, undo reverts
 * - snooze ("Morgen erinnern", "In 3 Std." …): moves the due date, undo restores the old one
 */

export interface ToDoActionItem {
    _id: Types.ObjectId | string;
    name?: string;
    dueDate?: Date | string | null;
    hasDueTime?: boolean | null;
    completed?: boolean;
    completionComment?: string;
    data?: unknown;
    dataType?: ToDoDataTypesEnum | string;
}

const idOf = (item: ToDoActionItem) => item._id as Types.ObjectId;

/** UI hooks: hide the item at once, show it again when undone or when the delete failed */
export interface DeleteUiHooks {
    hide: () => void;
    restore: () => void;
}

export async function deleteToDoWithUndo(item: ToDoActionItem, ui: DeleteUiHooks): Promise<UndoOutcome> {
    const id = idOf(item);
    const soft = await ToDoManagerApi.items.supportsRestore();
    ui.hide();
    if (soft) {
        try {
            await ToDoManagerApi.items.remove(id);
        } catch (e) {
            ui.restore();
            throw e;
        }
        return showUndoToast({
            text: 'To-do gelöscht',
            sub: item.name,
            undo: async () => {
                await ToDoManagerApi.items.restore(id);
                ui.restore();
            },
        });
    }
    // Old backend: hard delete, so it only runs after the toast unless undone (not when the tab is closed)
    const outcome = await showUndoToast({
        text: 'To-do wird gelöscht',
        sub: item.name,
        undo: () => ui.restore(),
        commit: () => ToDoManagerApi.items.remove(id),
        errorText: 'Das To-do konnte nicht gelöscht werden.',
    });
    if (outcome === 'failed') {
        ui.restore();
    }
    return outcome;
}

/** To-dos that expect an answer (Ja/Nein, Text …) are completed with the dialog */
export const needsCompletionDialog = (item: ToDoActionItem): boolean =>
    !!item.dataType && item.dataType !== ToDoDataTypesEnum.NONE;

/** Completes without dialog and offers undo; `onChange(completed)` updates the UI (also after undo) */
export async function completeToDoWithUndo(item: ToDoActionItem, onChange?: (completed: boolean) => void): Promise<void> {
    const id = idOf(item);
    await ToDoManagerApi.items.complete(id, {value: true});
    onChange?.(true);
    showUndoToast({
        text: 'To-do erledigt',
        sub: item.name,
        undo: async () => {
            await ToDoManagerApi.items.complete(id, {value: false});
            onChange?.(false);
        },
    });
}

/** Shows undo after the completion dialog has completed the to-do */
export function offerUndoCompletion(item: ToDoActionItem, onChange?: (completed: boolean) => void): void {
    const id = idOf(item);
    showUndoToast({
        text: 'To-do erledigt',
        sub: item.name,
        undo: async () => {
            await ToDoManagerApi.items.complete(id, {value: false});
            onChange?.(false);
        },
    });
}

/** Reopens a completed to-do; undo completes it again with the former comment and answer */
export async function reopenToDoWithUndo(item: ToDoActionItem, onChange?: (completed: boolean) => void): Promise<void> {
    const id = idOf(item);
    await ToDoManagerApi.items.complete(id, {value: false});
    onChange?.(false);
    showUndoToast({
        text: 'To-do wieder geöffnet',
        sub: item.name,
        undo: async () => {
            await ToDoManagerApi.items.complete(id, {
                value: true,
                comment: item.completionComment || undefined,
                data: item.data,
            });
            onChange?.(true);
        },
    });
}

export type ToDoSnoozePreset = 'in1h' | 'in3h' | 'tomorrow' | 'nextWeek';

export const TO_DO_SNOOZE_OPTIONS: Array<{ value: ToDoSnoozePreset; text: string; icon: string }> = [
    {value: 'in3h', text: 'In 3 Std.', icon: 'mdi-clock-fast'},
    {value: 'tomorrow', text: 'Morgen', icon: 'mdi-weather-sunset-up'},
    {value: 'nextWeek', text: 'Nächste Woche', icon: 'mdi-calendar-arrow-right'},
];

/** New due date of a preset; to-dos without time stay date-only for day presets */
export function snoozedDue(item: ToDoActionItem, preset: ToDoSnoozePreset, now = moment()): { dueDate: Date; hasDueTime: boolean } {
    if (preset === 'in1h' || preset === 'in3h') {
        return {dueDate: now.clone().add(preset === 'in1h' ? 1 : 3, 'hours').startOf('minute').toDate(), hasDueTime: true};
    }
    const day = preset === 'tomorrow' ? now.clone().add(1, 'day') : now.clone().add(1, 'week').startOf('isoWeek');
    if (item.hasDueTime) {
        return {dueDate: day.hour(8).minute(0).second(0).millisecond(0).toDate(), hasDueTime: true};
    }
    return {dueDate: day.startOf('day').toDate(), hasDueTime: false};
}

const SNOOZE_TEXT: Record<ToDoSnoozePreset, string> = {
    in1h: 'in 1 Std.',
    in3h: 'in 3 Std.',
    tomorrow: 'auf morgen',
    nextWeek: 'auf nächste Woche',
};

/** Moves the due date; undo restores the former one. `onChange` receives the due date now valid. */
export async function snoozeToDoWithUndo(
    item: ToDoActionItem,
    preset: ToDoSnoozePreset,
    onChange?: (due: { dueDate: Date | null; hasDueTime: boolean }) => void,
): Promise<void> {
    const id = idOf(item);
    const before = {dueDate: item.dueDate ? new Date(item.dueDate) : null, hasDueTime: !!item.dueDate && !!item.hasDueTime};
    const after = snoozedDue(item, preset);
    await ToDoManagerApi.items.update(id, after);
    onChange?.(after);
    showUndoToast({
        text: `Fälligkeit ${SNOOZE_TEXT[preset]} verschoben`,
        sub: item.name,
        undo: async () => {
            await ToDoManagerApi.items.update(id, before);
            onChange?.(before);
        },
    });
}
